import { makeUseAxios } from 'axios-hooks';

import { API, defaultApiTransformResponse } from 'src/app/services/axios';
import type { ICustomerLeadContactCreate } from 'src/types/customers/ICustomerLeadContactCreate';
import type { ICustomerLeadContactUpdate } from 'src/types/customers/ICustomerLeadContactUpdate';
import type { ICustomerLeadProduct } from 'src/types/customers/ICustomerLeadProduct';
import { fromApiCustomerLeadProductResponse } from 'src/types/customers/ICustomerLeadProductResponse';
import type { ICustomerLeadProductPartialUpdate } from 'src/types/customers/ICustomerLeadProductUpdate';

const useAxiosApi = makeUseAxios({
  axios: API,
});

const customerProductLeadTransformResponse = defaultApiTransformResponse.concat(fromApiCustomerLeadProductResponse);

const BASE_PATH = '/leads/customers';

export const customersApi = {
  useGetCustomerProductLeadQuery: (customerProductLeadId: string) =>
    useAxiosApi<ICustomerLeadProduct>({
      url: `${BASE_PATH}/${customerProductLeadId}`,
      method: 'GET',
      transformResponse: customerProductLeadTransformResponse,
    }),

  useManualGetCustomerProductLeadQuery: (customerProductLeadId: string) =>
    useAxiosApi<ICustomerLeadProduct>(
      {
        url: `${BASE_PATH}/${customerProductLeadId}`,
        method: 'GET',
        transformResponse: customerProductLeadTransformResponse,
      },
      {
        manual: true,
      }
    ),

  usePartialUpdateCustomerProductLeadMutation: (customerProductLeadId: string) =>
    useAxiosApi<ICustomerLeadProduct, ICustomerLeadProductPartialUpdate>(
      {
        url: `${BASE_PATH}/${customerProductLeadId}`,
        method: 'PATCH',
        responseType: 'json',
        transformResponse: customerProductLeadTransformResponse,
        // Lead product update data (`data`) will be set in refetch function
      },
      {
        manual: true,
      }
    ),

  useCreateCustomerLeadContactMutation: (customerProductLeadId: string) =>
    useAxiosApi<void, ICustomerLeadContactCreate>(
      {
        url: `${BASE_PATH}/${customerProductLeadId}/contacts`,
        method: 'POST',
        responseType: 'json',
        // Lead contact create data (`data`) will be set in mutate function
      },
      {
        manual: true,
      }
    ),

  useUpdateCustomerLeadContactMutation:
    (customerProductLeadId: string) =>
    async ({ id, ...bodyParams }: ICustomerLeadContactUpdate) =>
      await API.put<void>(`${BASE_PATH}/${customerProductLeadId}/contacts/${id}`, { ...bodyParams }),

  useDeleteCustomerLeadContactMutation: (customerProductLeadId: string) => async (customerLeadContactId: string) =>
    await API.delete<void>(`${BASE_PATH}/${customerProductLeadId}/contacts/${customerLeadContactId}`),
};
